import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Wand2, Zap, Camera, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import Navbar from '../../components/layout/Navbar';

const sections = [
  {
    title: 'Effective Prompt Structure',
    icon: Wand2,
    description: 'Learn the anatomy of a perfect AI prompt',
    content: [
      'Start with the main subject: "A modern smartphone on a clean white background"',
      'Add style descriptors: "minimalist, professional, high-end photography"',
      'Include lighting details: "soft natural lighting, subtle shadows"',
      'Specify composition: "centered composition, rule of thirds"',
      'Add quality modifiers: "high resolution, sharp focus, detailed"',
      'End with technical specs: "shot with professional camera, studio lighting"'
    ],
    example: '"A luxury watch on marble surface, minimalist style, soft natural lighting, centered composition, high resolution, professional photography"'
  },
  {
    title: 'Style and Mood Keywords',
    icon: Sparkles,
    description: 'Master the vocabulary that shapes your AI-generated content',
    content: [
      'Minimalist: clean, simple, uncluttered, modern, sleek',
      'Luxury: premium, elegant, sophisticated, high-end, refined',
      'Vintage: retro, classic, aged, nostalgic, timeless',
      'Vibrant: colorful, bright, energetic, bold, dynamic',
      'Moody: dramatic, atmospheric, cinematic, dark, mysterious',
      'Natural: organic, earthy, authentic, rustic, warm'
    ],
    example: 'Style: "cinematic, moody, dramatic lighting" vs "bright, cheerful, vibrant colors"'
  },
  {
    title: 'Product Photography Prompts',
    icon: Camera,
    description: 'Specialized prompts for stunning product visuals',
    content: [
      'Hero shots: "Product floating on gradient background, studio lighting"',
      'Lifestyle: "Product in natural environment, lifestyle photography"',
      'Detail shots: "Close-up macro photography, texture details visible"',
      'Flat lay: "Top-down view, flat lay style, organized composition"',
      'In-use: "Product being used by person, natural interaction"',
      'Comparison: "Multiple products side by side, clean comparison"'
    ],
    example: '"Wireless headphones on wooden desk, lifestyle photography, warm natural lighting, shallow depth of field, professional product photography"'
  },
  {
    title: 'Advanced Prompting Techniques',
    icon: Zap,
    description: 'Pro techniques for precise control over AI generation',
    content: [
      'Negative prompts: Specify what you DON\'T want in the image',
      'Weight modifiers: Use (word:1.2) to emphasize or (word:0.8) to de-emphasize',
      'Style mixing: Combine multiple artistic styles for unique results',
      'Aspect ratios: Specify dimensions like "16:9 aspect ratio" or "square format"',
      'Camera angles: "low angle shot", "bird\'s eye view", "close-up portrait"',
      'Color schemes: "monochromatic blue", "warm color palette", "high contrast"'
    ],
    example: 'Advanced: "Modern laptop (sleek:1.3) on desk, (cluttered:0.5), professional photography, 16:9 aspect ratio, --no shadows"'
  }
];

const AIPromptingGuide: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50/30">
      <Navbar />
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/resources" className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Resources
            </Link>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              AI Prompting Guide
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl">
              Master the art of AI prompting to generate exactly the content you envision with our powerful AI tools.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="bg-white/70 backdrop-blur-sm border border-gray-200/60 shadow-lg">
                  <CardHeader>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold text-gray-900">
                          {section.title}
                        </CardTitle>
                        <CardDescription className="text-gray-600">
                          {section.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-4">
                      {section.content.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start space-x-3">
                          <Sparkles className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                    {section.example && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-4">
                        <p className="text-sm font-medium text-purple-800 mb-2">Example:</p>
                        <p className="text-purple-700 italic">{section.example}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Quick Reference */}
        <motion.div
          className="mt-12"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
            <CardHeader>
              <CardTitle className="text-2xl font-bold flex items-center">
                <Zap className="w-6 h-6 mr-3" />
                Quick Prompt Formula
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-purple-100 text-lg font-mono">
                  [Subject] + [Style] + [Lighting] + [Composition] + [Quality]
                </p>
                <p className="text-purple-200 text-sm mt-2">
                  Example: "Modern smartphone + minimalist style + soft lighting + centered + high resolution"
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="bg-white/70 backdrop-blur-sm border border-gray-200/60 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to Try AI Prompting?</h2>
            <p className="text-gray-600 mb-6">
              Put these techniques into practice with our AI-powered Ad Studio.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/app/content-creation/ad-studio">
                <Button size="lg" className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white">
                  Try AI Generation
                </Button>
              </Link>
              <Link to="/resources">
                <Button size="lg" variant="outline">
                  More Guides
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AIPromptingGuide;